import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { catalogApi, type Catalog, type ClassificationResult, type ClassificationSuggestion } from '../../api/catalog';

export function AIClassificationReviewPage() {
  const [catalogs, setCatalogs] = useState<Catalog[]>([]);
  const [catalogId, setCatalogId] = useState('');
  const [productId, setProductId] = useState('');
  const [productName, setProductName] = useState('');
  const [brand, setBrand] = useState('');
  const [description, setDescription] = useState('');
  const [suggestions, setSuggestions] = useState<ClassificationSuggestion[]>([]);
  const [lastResult, setLastResult] = useState<ClassificationResult | null>(null);
  const [error, setError] = useState('');

  useEffect(() => {
    void catalogApi.listCatalogs().then((items) => {
      setCatalogs(items);
      if (items[0]?.id) setCatalogId(items[0].id);
    });
  }, []);

  const suggest = async () => {
    if (!productId.trim() || !productName.trim() || !catalogId) {
      setError('Product ID, name, and catalog are required');
      return;
    }
    setError('');
    try {
      const res = await catalogApi.suggest({
        product_id: productId.trim(),
        product_name: productName.trim(),
        brand: brand.trim() || undefined,
        description: description.trim() || undefined,
        catalog_id: catalogId,
      });
      setSuggestions(res);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Suggestion failed');
    }
  };

  const accept = async (s: ClassificationSuggestion) => {
    try {
      const res = await catalogApi.classify({
        product_id: productId.trim(),
        category_id: s.category_id,
        catalog_id: catalogId,
        source: 'ai',
        score: s.score,
        confidence: s.confidence,
      });
      setLastResult(res);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Classification failed');
    }
  };

  return (
    <div className="mx-auto max-w-4xl p-6">
      <Link to="/catalog/classification" className="text-sm text-slate-600 hover:underline">← Classification</Link>
      <h1 className="mt-4 text-2xl font-semibold">AI Classification Review</h1>
      <p className="mt-1 text-sm text-slate-600">Review AI suggestions and approve or override classifications.</p>
      <div className="mt-6 space-y-3 rounded-xl border bg-white p-6">
        <select value={catalogId} onChange={(e) => setCatalogId(e.target.value)} className="w-full rounded-lg border px-3 py-2 text-sm">
          {catalogs.map((c) => (
            <option key={c.id} value={c.id}>{c.name}</option>
          ))}
        </select>
        <input value={productId} onChange={(e) => setProductId(e.target.value)} placeholder="Product ID" className="w-full rounded-lg border px-3 py-2 text-sm" />
        <input value={productName} onChange={(e) => setProductName(e.target.value)} placeholder="Product name" className="w-full rounded-lg border px-3 py-2 text-sm" />
        <input value={brand} onChange={(e) => setBrand(e.target.value)} placeholder="Brand (optional)" className="w-full rounded-lg border px-3 py-2 text-sm" />
        <textarea value={description} onChange={(e) => setDescription(e.target.value)} placeholder="Description (optional)" className="w-full rounded-lg border px-3 py-2 text-sm" rows={2} />
        {error && <p className="text-sm text-red-600">{error}</p>}
        <button type="button" onClick={() => void suggest()} className="rounded-lg bg-slate-900 px-4 py-2 text-sm text-white">
          Get AI suggestions
        </button>
      </div>
      {suggestions.length > 0 && (
        <div className="mt-6 overflow-x-auto rounded-xl border bg-white">
          <table className="min-w-full text-sm">
            <thead className="border-b bg-slate-50 text-left text-slate-600">
              <tr>
                <th className="px-4 py-3">Category</th>
                <th className="px-4 py-3">Path</th>
                <th className="px-4 py-3">Score</th>
                <th className="px-4 py-3">Confidence</th>
                <th className="px-4 py-3" />
              </tr>
            </thead>
            <tbody>
              {suggestions.map((s) => (
                <tr key={s.category_id} className="border-b">
                  <td className="px-4 py-3 font-medium">{s.category_name}</td>
                  <td className="px-4 py-3">{s.path}</td>
                  <td className="px-4 py-3">{(s.score * 100).toFixed(0)}%</td>
                  <td className="px-4 py-3">{(s.confidence * 100).toFixed(0)}%</td>
                  <td className="px-4 py-3">
                    <button type="button" onClick={() => void accept(s)} className="text-slate-700 hover:underline">
                      Accept
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {lastResult && (
        <p className="mt-4 text-sm text-emerald-700">
          Classified product {lastResult.product_id} → category {lastResult.category_id} (confidence {(lastResult.confidence * 100).toFixed(0)}%)
        </p>
      )}
    </div>
  );
}
